import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Save } from "lucide-react";
import { extractSvgs } from "@/components/MathText";

// Renders assistant markdown (#, ##, **bold**, lists, tables, code) into a clean,
// colourful, well-structured layout so guidance replies are interactive & readable.
// Any inline <svg> blackboard sketches the AI draws are pulled out and rendered as
// figures (never shown as raw markup). When `onSaveSketch` is given, each figure
// gets a "Save to My Notes" action so teachers can reuse the sketch later.
export function MarkdownMessage({ content, className = "", onSaveSketch }) {
  const { text, svgs } = extractSvgs(content || "");
  return (
    <div className={`text-sm leading-relaxed ${className}`} data-testid="markdown-message">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ node, ...p }) => <h1 className="text-lg font-bold text-indigo-700 dark:text-indigo-300 mt-3 mb-1.5 first:mt-0" style={{ fontFamily: "Outfit" }} {...p} />,
          h2: ({ node, ...p }) => <h2 className="text-base font-bold text-sky-700 dark:text-sky-300 mt-3 mb-1.5 first:mt-0" style={{ fontFamily: "Outfit" }} {...p} />,
          h3: ({ node, ...p }) => <h3 className="text-sm font-bold text-emerald-700 dark:text-emerald-300 mt-2.5 mb-1 first:mt-0" {...p} />,
          h4: ({ node, ...p }) => <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mt-2 mb-1" {...p} />,
          p: ({ node, ...p }) => <p className="my-1.5" {...p} />,
          strong: ({ node, ...p }) => <strong className="font-semibold text-slate-900 dark:text-white" {...p} />,
          em: ({ node, ...p }) => <em className="italic text-slate-700 dark:text-slate-200" {...p} />,
          ul: ({ node, ...p }) => <ul className="list-disc pl-5 my-1.5 space-y-1 marker:text-indigo-400" {...p} />,
          ol: ({ node, ...p }) => <ol className="list-decimal pl-5 my-1.5 space-y-1 marker:text-indigo-400 marker:font-semibold" {...p} />,
          li: ({ node, ...p }) => <li className="pl-1" {...p} />,
          blockquote: ({ node, ...p }) => <blockquote className="border-l-4 border-indigo-300 dark:border-indigo-700 pl-3 my-2 italic text-slate-600 dark:text-slate-300 bg-indigo-50/50 dark:bg-indigo-950/30 py-1 rounded-r" {...p} />,
          code: ({ node, inline, ...p }) => inline
            ? <code className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-rose-600 dark:text-rose-300 text-[0.85em] font-mono" {...p} />
            : <code className="block p-3 rounded-lg bg-slate-900 text-slate-100 text-[0.85em] font-mono overflow-x-auto my-2" {...p} />,
          a: ({ node, ...p }) => <a className="text-indigo-600 dark:text-indigo-300 underline" target="_blank" rel="noreferrer" {...p} />,
          hr: () => <hr className="my-3 border-slate-200 dark:border-slate-700" />,
          table: ({ node, ...p }) => <div className="overflow-x-auto my-2"><table className="w-full text-xs border-collapse" {...p} /></div>,
          th: ({ node, ...p }) => <th className="border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-2 py-1 text-left font-semibold" {...p} />,
          td: ({ node, ...p }) => <td className="border border-slate-200 dark:border-slate-700 px-2 py-1" {...p} />,
        }}
      >
        {text || ""}
      </ReactMarkdown>
      {svgs.length > 0 && (
        <div className="mt-2 space-y-2" data-testid="markdown-figures">
          {svgs.map((svg, si) => (
            <div key={si} data-testid={`markdown-figure-${si}`}>
              <div
                className="block w-full max-w-md rounded-xl border border-slate-200 dark:border-slate-700 bg-white p-2 [&_svg]:block [&_svg]:w-full [&_svg]:h-auto"
                dangerouslySetInnerHTML={{ __html: svg }}
              />
              {onSaveSketch && (
                <button
                  type="button"
                  onClick={() => onSaveSketch(svg, si)}
                  className="mt-1 inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-indigo-200 dark:border-indigo-700 text-indigo-600 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors"
                  data-testid={`sketch-save-${si}`}
                >
                  <Save className="h-3.5 w-3.5" /> Save to My Notes
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
